// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, FlatList, Modal, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import DatePicker from 'react-native-date-picker';
import * as Location from 'expo-location';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text } from '@/components/Themed';
import { t } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getSharedBffWebSocket, TourCreatedEvent } from '@/services/bffWebSocket';
import { createAppLogger } from '@/services/appLogger';
import { getCurrentTourId, setCurrentTourId } from '@/services/currentTour';
import { setCurrentRoundId } from '@/services/currentRound';
import { useRoundState } from '@/stores/roundState';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import type {
  AvailablePlayer,
  Course,
  Player,
  PlayerScpPrefill,
  PlayersResponse,
  TourRoundSummary,
  TourSummary,
} from '@/features/round-init/types';
import {
  buildEmptyScpIds,
  buildPlayerOrder,
  deriveInitialTourId,
  normalizeDebugParam,
} from '@/features/round-init/utils';

export default function PlayersScreen() {
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { debug: debugParam, tourId: tourIdParam } = useLocalSearchParams<{ debug?: string; tourId?: string }>();
  const debug = useMemo(() => normalizeDebugParam(debugParam), [debugParam]);
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'players-screen',
      }),
    [apiBaseUrl, user?.token]
  );
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [courseError, setCourseError] = useState<string | null>(null);
  const [coursePickerOpen, setCoursePickerOpen] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [players, setPlayers] = useState<Player[]>([{ id: generateId(), name: '', scp: '' }]);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [playerLoading, setPlayerLoading] = useState(true);
  const [savingPlayers, setSavingPlayers] = useState(false);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const roundInitiateRequestIdRef = useRef<string | null>(null);
  const roundAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [roundName, setRoundName] = useState('');
  const [roundDescription, setRoundDescription] = useState('');
  const [roundDescriptionOpen, setRoundDescriptionOpen] = useState(false);
  const [holesCount, setHolesCount] = useState(18);
  const [roundPlayDate, setRoundPlayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [playDatePickerOpen, setPlayDatePickerOpen] = useState(false);
  const initialTourId = useMemo(() => deriveInitialTourId(tourIdParam, getCurrentTourId()), [tourIdParam]);
  const [tourId, setTourId] = useState<string | null>(initialTourId);
  const [tourName, setTourName] = useState<string | null>(null);
  const roundId = useRoundState((state) => state.roundId);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const setHole = useRoundState((state) => state.setHole);
  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>([]);
  const [currentUserPlayerId, setCurrentUserPlayerId] = useState<string | null>(null);
  const [playerPickerOpen, setPlayerPickerOpen] = useState(false);
  const [pickerIndex, setPickerIndex] = useState<number | null>(null);
  const scpPrefillKeyRef = useRef<string | null>(null);
  const [activeRounds, setActiveRounds] = useState<TourRoundSummary[]>([]);
  const [activeRoundsLoading, setActiveRoundsLoading] = useState(false);
  const [activeRoundsError, setActiveRoundsError] = useState<string | null>(null);
  const [finishedRounds, setFinishedRounds] = useState<TourRoundSummary[]>([]);
  const [finishedRoundsLoading, setFinishedRoundsLoading] = useState(false);
  const [openRoundMenuId, setOpenRoundMenuId] = useState<string | null>(null);
  const availablePlayerIdSet = useMemo(() => new Set(availablePlayers.map((p) => p.id)), [availablePlayers]);
  const emptyScpIds = useMemo(() => buildEmptyScpIds(players, availablePlayerIdSet), [availablePlayerIdSet, players]);
  const emptyScpKey = useMemo(() => emptyScpIds.join(','), [emptyScpIds]);

  const parseDateInput = (value: string) => {
    if (!value) return null;
    const parts = value.split('-').map((item) => Number.parseInt(item, 10));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };
  const formatDateInput = (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId);
    }
  }, [tourId]);

  useEffect(() => {
    if (!isFocused) return;
    const raw = Array.isArray(tourIdParam) ? tourIdParam[0] : tourIdParam;
    if (raw) return;
    const stored = getCurrentTourId();
    if (stored && stored !== tourId) {
      setTourId(stored);
    }
  }, [isFocused, tourId, tourIdParam]);

  useEffect(() => {
    if (!tourId || !user?.token || !isNavigationReady) return;
    const loadTourName = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as TourSummary;
        setTourName(payload.tourName ?? payload.name ?? null);
      } catch (err) {
        logToBff('players.tour.name.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
      }
    };
    loadTourName();
  }, [apiBaseUrl, isNavigationReady, logToBff, tourId, user?.token]);

  useEffect(() => {
    if (roundId) {
      setCurrentRoundId(roundId);
    }
  }, [roundId]);

  const fetchCourses = useCallback(
    async () => {
      if (!isNavigationReady || !user?.token) return;
      setLoadingCourses(true);
      setCourseError(null);
      try {
        if (!coords) {
          setCourseError('Kunde inte hitta platsdata.');
          setLoadingCourses(false);
          return;
        }
        const response = await fetch(`${apiBaseUrl}/api/geodata/courses?lat=${coords.latitude}&lon=${coords.longitude}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error('Kunde inte hämta banor.');
        }
        const data = (await response.json()) as Course[];
        setCourses(data);
        const chalmers = data.find((course) => course.name.toLowerCase().includes('chalmers'));
        setSelectedCourseId((prev) => {
          if (prev && data.some((course) => course.id === prev)) {
            return prev;
          }
          return chalmers?.id ?? data[0]?.id ?? null;
        });
        logToBff('players.courses.loaded', { count: data.length });
      } catch (err) {
        setCourseError('Kunde inte hämta banor.');
        logToBff('players.courses.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        setLoadingCourses(false);
      }
    },
    [apiBaseUrl, coords, isNavigationReady, user?.token, logToBff]
  );

  const fetchPlayers = useCallback(async () => {
    if (!isNavigationReady) return;
    try {
      if (!user?.token) {
        setPlayerLoading(false);
        return;
      }
      setPlayerLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/players`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error(`${t('players.loadError')} (${response.status})`);
      }
      const data = (await response.json()) as PlayersResponse;
      const rawPlayers = data.players ?? data.Players ?? [];
      const incomingPlayers = rawPlayers
        .map((player) => ({
          id: player.id,
          name: player.name,
          scp: '',
        }))
        .slice(0, 12);
      const incomingAvailable = rawPlayers.map((p) => ({
        id: p.id,
        name: p.name,
        userId: (p as { userId?: string | null }).userId ?? (p as { UserId?: string | null }).UserId ?? null,
      }));
      const currentUserId = user?.id ?? null;
      const currentUserPlayer = currentUserId
        ? incomingAvailable.find((p) => p.userId === currentUserId)
        : undefined;
      setCurrentUserPlayerId(currentUserPlayer?.id ?? null);
      const orderedPlayers = buildPlayerOrder(incomingPlayers, currentUserPlayer, generateId);
      setPlayers(orderedPlayers);
      setAvailablePlayers(incomingAvailable);
      const incomingTourId = data.tourId ?? data.TourId;
      if (!tourId && incomingTourId && incomingTourId !== '00000000-0000-0000-0000-000000000000') {
        setTourId(incomingTourId);
      }
      logToBff('players.snapshot.loaded', { playerCount: incomingPlayers.length, tourId: incomingTourId });
    } catch (err) {
      setPlayerError(t('players.loadError'));
      logToBff('players.snapshot.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
    } finally {
      setPlayerLoading(false);
    }
  }, [apiBaseUrl, isNavigationReady, user?.token, logToBff, tourId, user?.id]);

  useEffect(() => {
    if (!isNavigationReady) return;
    fetchPlayers();
  }, [fetchPlayers, isNavigationReady]);

  useEffect(() => {
    if (!isNavigationReady) return;
    fetchCourses();
  }, [fetchCourses, isNavigationReady]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ tourId });
  }, [apiBaseUrl, isFocused, isNavigationReady, tourId, setRoundId]);

  const fetchActiveRounds = useCallback(async () => {
    if (!tourId || !isNavigationReady) return;
    setActiveRoundsLoading(true);
    setActiveRoundsError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=draft,active,playing`, {
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) {
        throw new Error(`Failed to load rounds (${response.status})`);
      }
      const data = (await response.json()) as TourRoundSummary[];
      setActiveRounds(data);
    } catch (err) {
      setActiveRoundsError(t('players.roundsLoadError'));
      logToBff('players.rounds.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
    } finally {
      setActiveRoundsLoading(false);
    }
  }, [apiBaseUrl, isNavigationReady, logToBff, tourId, user?.token]);

  const fetchFinishedRounds = useCallback(async () => {
    if (!tourId || !isNavigationReady) return;
    setFinishedRoundsLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=finished`, {
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) {
        throw new Error(`Failed to load finished rounds (${response.status})`);
      }
      const data = (await response.json()) as TourRoundSummary[];
      setFinishedRounds(data);
    } catch (err) {
      logToBff('players.rounds.finished.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
    } finally {
      setFinishedRoundsLoading(false);
    }
  }, [apiBaseUrl, isNavigationReady, logToBff, tourId, user?.token]);

  useEffect(() => {
    fetchActiveRounds();
  }, [fetchActiveRounds]);

  useEffect(() => {
    fetchFinishedRounds();
  }, [fetchFinishedRounds]);

  useEffect(() => {
    if (!isNavigationReady || !selectedCourseId || emptyScpIds.length === 0) return;
    const key = `${selectedCourseId}:${emptyScpKey}`;
    if (scpPrefillKeyRef.current === key) return;
    scpPrefillKeyRef.current = key;
    const controller = new AbortController();

    const fetchPrefill = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/players/scp/latest?courseId=${encodeURIComponent(selectedCourseId)}&playerIds=${emptyScpKey}`,
          {
            headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          logToBff('players.scp.prefill.failed', { status: response.status });
          return;
        }
        const data = (await response.json()) as PlayerScpPrefill[];
        if (!data.length) return;
        const scpMap = new Map<string, string>();
        data.forEach((entry) => {
          const playerId = entry.playerId ?? entry.PlayerId;
          const scp = entry.scp ?? entry.Scp;
          if (!playerId || !scp) return;
          scpMap.set(playerId, scp);
        });
        if (scpMap.size === 0) return;
        setPlayers((prev) =>
          prev.map((player) => {
            if (player.scp.trim()) return player;
            const scp = scpMap.get(player.id);
            return scp ? { ...player, scp } : player;
          })
        );
        logToBff('players.scp.prefill.applied', { count: scpMap.size, courseId: selectedCourseId });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        logToBff('players.scp.prefill.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
      }
    };

    fetchPrefill();
    return () => controller.abort();
  }, [apiBaseUrl, emptyScpIds.length, emptyScpKey, isNavigationReady, logToBff, selectedCourseId, user?.token]);

  useEffect(() => {
    if (!coords || !isNavigationReady) return;
    fetchCourses();
  }, [coords, fetchCourses, isNavigationReady]);

  useEffect(() => {
    const requestLocation = async () => {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          setLocationDenied(true);
          setCoords({ lat: 57.7089, lon: 11.9746 });
          return;
        }
        const position = await Location.getCurrentPositionAsync({});
        setCoords({ lat: position.coords.latitude, lon: position.coords.longitude });
      } catch {
        setLocationDenied(true);
        setCoords({ lat: 57.7089, lon: 11.9746 });
      }
    };
    requestLocation().catch(() => {
      setLocationDenied(true);
      setCoords({ lat: 57.7089, lon: 11.9746 });
    });
  }, []);

  const ackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      if ((message as TourCreatedEvent).type === 'tour.created') {
        // No-op on players page; tour navigation happens after explicit update/save.
        logToBff('players.tour.created.ignored', {});
        return;
      }
      if ((message as { type?: string }).type === 'tour.update.ack') {
        const payload = message as { tourId?: string; requestId?: string };
        if (pendingRequestId && payload.requestId && payload.requestId !== pendingRequestId) {
          return;
        }
        if (ackTimeoutRef.current) {
          clearTimeout(ackTimeoutRef.current);
          ackTimeoutRef.current = null;
        }
        setSavingPlayers(false);
        setPendingRequestId(null);
        logToBff('players.tour.update.ack', { tourId: payload.tourId ?? tourId, requestId: payload.requestId });
        if (payload.tourId && payload.tourId !== '00000000-0000-0000-0000-000000000000') {
          setTourId(payload.tourId);
        }
      }
      if ((message as { type?: string }).type === 'round.created') {
        const payload = message as {
          roundId?: string;
          RoundId?: string;
          tourId?: string;
          TourId?: string;
        };
        if (roundAckTimeoutRef.current) {
          clearTimeout(roundAckTimeoutRef.current);
          roundAckTimeoutRef.current = null;
        }
        roundInitiateRequestIdRef.current = null;
        setSavingPlayers(false);
        setPendingRequestId(null);
        const incomingRoundId =
          (payload.roundId ?? payload.RoundId) &&
          (payload.roundId ?? payload.RoundId) !== '00000000-0000-0000-0000-000000000000'
            ? (payload.roundId ?? payload.RoundId)
            : null;
        const incomingTourId =
          (payload.tourId ?? payload.TourId) &&
          (payload.tourId ?? payload.TourId) !== '00000000-0000-0000-0000-000000000000'
            ? (payload.tourId ?? payload.TourId)
            : null;
        if (incomingRoundId) {
          setRoundId(incomingRoundId);
        }
        if (incomingTourId) {
          setTourId(incomingTourId);
        }
        logToBff('players.round.created', { roundId: incomingRoundId, tourId: incomingTourId });
        const debugSuffix = debug ? '?debug=true' : '';
        if (incomingRoundId) {
          router.push(`/round/${incomingRoundId}/select-games${debugSuffix}`);
        }
      }
    });
    const unsubscribeError = socket.onError((msg) => {
      setPlayerError(msg);
      if (ackTimeoutRef.current) {
        clearTimeout(ackTimeoutRef.current);
        ackTimeoutRef.current = null;
      }
      setSavingPlayers(false);
      setPendingRequestId(null);
    });
    return () => {
      unsubscribe();
      unsubscribeError();
      if (ackTimeoutRef.current) {
        clearTimeout(ackTimeoutRef.current);
        ackTimeoutRef.current = null;
      }
      if (roundAckTimeoutRef.current) {
        clearTimeout(roundAckTimeoutRef.current);
        roundAckTimeoutRef.current = null;
      }
    };
  }, [apiBaseUrl, debug, pendingRequestId, router, tourId, user?.token, logToBff, setRoundId]);

  const updateRoundStatus = useCallback(
    (roundId: string, status: string) => {
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user?.token ?? null);
      socket.send({ type: 'round.status.update', roundId, status });
      setActiveRounds((prev) =>
        status === 'cancelled'
          ? prev.filter((round) => round.id !== roundId)
          : prev.map((round) =>
              round.id === roundId ? { ...round, status, statusUpdatedAt: new Date().toISOString() } : round
            )
      );
      setFinishedRounds((prev) =>
        status === 'cancelled' ? prev.filter((round) => round.id !== roundId) : prev
      );
      setOpenRoundMenuId(null);
    },
    [apiBaseUrl, user?.token]
  );

  const formatRoundStatus = useCallback(
    (round: TourRoundSummary) => {
      if (!round.status) return null;
      const normalized = round.status.toLowerCase();
      if (normalized === 'active' || normalized === 'finished') {
        const timestamp = round.statusUpdatedAt ?? round.createdAt;
        if (!timestamp) {
          return t('players.roundStatus', { status: round.status });
        }
        const dateLabel = new Date(timestamp).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
        return t('players.roundStatusWithDate', { status: round.status, date: dateLabel });
      }
      return t('players.roundStatus', { status: round.status });
    },
    []
  );

  const truncateRoundName = useCallback((value?: string | null) => {
    const name = value ?? '';
    if (name.length <= 27) return name;
    return `${name.slice(0, 27)}…`;
  }, []);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/login" />;

  const selectedCourse = courses.find((course) => course.id === selectedCourseId) ?? null;

  const addPlayerRow = () => {
    setPlayerError(null);
    setPlayers((prev) => {
      if (prev.length >= 12) return prev;
      return [...prev, { id: generateId(), name: '', scp: '' }];
    });
  };

  const removePlayerRow = (index: number) => {
    setPlayerError(null);
    setPlayers((prev) => {
      if (currentUserPlayerId && prev[index]?.id === currentUserPlayerId) {
        return prev;
      }
      if (prev.length === 1) {
        return [{ id: generateId(), name: '', scp: '' }];
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const selectExistingPlayer = (index: number, player: AvailablePlayer) => {
    setPlayerError(null);
    setPlayers((prev) =>
      prev.map((p, idx) => (idx === index ? { ...p, id: player.id, name: player.name } : p))
    );
  };

  const handleSelectGames = () => {
    const namedPlayers = players
      .map((player) => ({ ...player, name: player.name.trim(), scp: player.scp.trim() }))
      .filter((player) => player.name.length);
    if (namedPlayers.length === 0) {
      setPlayerError(t('home.validationRequired'));
      logToBff('players.validation.missingPlayers', { playerCount: players.length });
      return;
    }
    if (namedPlayers.some((player) => !player.scp)) {
      setPlayerError(t('home.scpRequired'));
      logToBff('players.validation.missingScp', { playerCount: namedPlayers.length });
      return;
    }
    if (!selectedCourse) {
      setCourseError(t('course.select'));
      logToBff('players.validation.missingCourse', {});
      return;
    }
    if (!tourId) {
      setPlayerError('Tour saknas – kan inte spara spelare.');
      return;
    }
    logToBff('players.handleSelectGames.clicked', { tourId, playerCount: namedPlayers.length });
    setPlayerError(null);
    setCourseError(null);
    const updateRequestId = generateId();
    setPendingRequestId(updateRequestId);
    setSavingPlayers(true);
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const payload = {
      type: 'tour.update',
      requestId: updateRequestId,
      tourId,
      tourName: selectedCourse.name ?? undefined,
      course: selectedCourse,
      players: namedPlayers.map((player) => ({
        id: player.id ?? generateId(),
        name: player.name,
        scp: player.scp,
      })),
    };
    const sendOrQueue = (msg: Record<string, unknown>) => {
      const didSend = socket.send(msg);
      logToBff(`players.ws.send.${msg.type as string}`, {
        sent: didSend,
        requestId: (msg as { requestId?: string }).requestId,
      });
      // When not sent, the shared client queues and will flush on connect; avoid manual retry to prevent duplicates.
    };

    if (ackTimeoutRef.current) {
      clearTimeout(ackTimeoutRef.current);
    }
    ackTimeoutRef.current = setTimeout(() => {
      setSavingPlayers(false);
      setPendingRequestId(null);
      setPlayerError('Ingen bekräftelse från BFF för tour.update.');
      logToBff('players.tour.update.timeout', { tourId, requestId: updateRequestId }, 'warn');
    }, 12000);

    sendOrQueue(payload);

    const roundRequestId = generateId();
    roundInitiateRequestIdRef.current = roundRequestId;
    const normalizedRoundName = roundName.trim() || selectedCourse.name;
    const roundPayload = {
      type: 'round.initiate',
      requestId: roundRequestId,
      tourId,
      name: normalizedRoundName,
      description: roundDescription.trim() || undefined,
      holesCount,
      playDate: roundPlayDate || new Date().toISOString().slice(0, 10),
      players: namedPlayers.map((player) => ({
        id: player.id ?? generateId(),
        scp: player.scp,
      })),
      course: selectedCourse,
      gameDefinitionIds: [],
    };
    if (roundAckTimeoutRef.current) {
      clearTimeout(roundAckTimeoutRef.current);
    }
    roundAckTimeoutRef.current = setTimeout(() => {
      if (roundInitiateRequestIdRef.current === roundRequestId) {
        logToBff('players.round.initiate.timeout', { tourId, requestId: roundRequestId }, 'warn');
      }
    }, 12000);
    sendOrQueue(roundPayload);
  };

  return (
    <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      <Text style={styles.title}>{t('players.title')}</Text>
      {debug ? (
        <Text style={styles.headlineDebug}>
          {`userId=${user?.id ?? ''} playerId=${currentUserPlayerId ?? ''}`}
        </Text>
      ) : null}
      {tourId ? <Text style={styles.tourName}>{tourName ?? t('players.tourNameFallback')}</Text> : null}
      {tourId ? (
        <View style={styles.roundsCard}>
          <View style={styles.roundsHeaderRow}>
            <Text style={styles.roundsTitle}>{t('players.activeRoundsTitle')}</Text>
            <Pressable
              style={[styles.refreshButton, activeRoundsLoading && styles.refreshButtonDisabled]}
              onPress={fetchActiveRounds}
              disabled={activeRoundsLoading}
            >
              <Text style={styles.refreshText}>{t('players.activeRoundsRefresh')}</Text>
            </Pressable>
          </View>
          {finishedRounds.length > 0 ? (
            <View style={styles.finishedRoundsBlock}>
              <Text style={styles.finishedRoundsTitle}>{t('players.finishedRoundsTitle')}</Text>
              {finishedRounds.filter((round) => round.status?.toLowerCase() !== 'cancelled').map((round) => (
                <Pressable
                  key={round.id}
                  style={[styles.roundRow, styles.roundRowFinished]}
                  onPress={() => {
                    setRoundId(round.id);
                    router.push(`/round/${round.id}/summary`);
                  }}
                >
                  <View style={styles.roundContent}>
                    <View>
                      <Text style={styles.roundName}>
                        {truncateRoundName(round.name ?? round.courseName ?? t('players.roundFallbackName'))}
                      </Text>
                      {round.status ? <Text style={styles.roundMeta}>{formatRoundStatus(round)}</Text> : null}
                    </View>
                    <Text style={styles.roundResults}>{t('players.roundResults')}</Text>
                  </View>
                </Pressable>
              ))}
            </View>
          ) : null}
          {activeRoundsError ? <Text style={styles.error}>{activeRoundsError}</Text> : null}
          {activeRounds.length > 0 ? (
            activeRounds.filter((round) => round.status?.toLowerCase() !== 'cancelled').map((round) => {
              const menuOpen = openRoundMenuId === round.id;
              return (
                <View key={round.id} style={[styles.roundRow, menuOpen && styles.roundRowMenuOpen]}>
                  <Pressable
                    style={styles.roundMenuButton}
                    onPress={() => setOpenRoundMenuId(menuOpen ? null : round.id)}
                    accessibilityLabel={t('players.roundMenuLabel')}
                  >
                    <Text style={styles.roundMenuIcon}>⋯</Text>
                  </Pressable>
                  <Pressable
                    style={styles.roundContent}
                    onPress={() => {
                      setRoundId(round.id);
                      setHole(1);
                      const params: string[] = [];
                      if (tourId) params.push(`tourId=${tourId}`);
                      const query = params.length ? `?${params.join('&')}` : '';
                      router.push(`/round/${round.id}/hole/1/compact${query}`);
                    }}
                  >
                    <View>
                      <Text style={styles.roundName}>
                        {truncateRoundName(round.name ?? round.courseName ?? t('players.roundFallbackName'))}
                      </Text>
                      {round.status ? <Text style={styles.roundMeta}>{formatRoundStatus(round)}</Text> : null}
                    </View>
                    <Text style={styles.roundOpen}>
                      {round.status?.toLowerCase() === 'active' ? t('players.roundContinue') : t('players.roundOpen')}
                    </Text>
                  </Pressable>
                  {menuOpen ? (
                    <View style={styles.roundMenu}>
                      <Pressable style={styles.roundMenuItem} onPress={() => updateRoundStatus(round.id, 'paused')}>
                        <Text style={styles.roundMenuText}>{t('players.roundMenuPause')}</Text>
                      </Pressable>
                      <Pressable style={styles.roundMenuItem} onPress={() => updateRoundStatus(round.id, 'finished')}>
                        <Text style={styles.roundMenuText}>{t('players.roundMenuFinish')}</Text>
                      </Pressable>
                      <Pressable style={styles.roundMenuItem} onPress={() => updateRoundStatus(round.id, 'cancelled')}>
                        <Text style={styles.roundMenuText}>{t('players.roundMenuCancel')}</Text>
                      </Pressable>
                    </View>
                  ) : null}
                </View>
              );
            })
          ) : (
            <Text style={styles.subtitle}>{t('players.activeRoundsEmpty')}</Text>
          )}
        </View>
      ) : null}
      <View style={styles.courseCard}>
        <View style={styles.courseHeader}>
          <Text style={styles.courseLabel}>{t('course.label')}</Text>
          <Pressable
            style={[styles.refreshButton, loadingCourses && styles.refreshButtonDisabled]}
            onPress={() => fetchCourses()}
            disabled={loadingCourses}
          >
            <Text style={styles.refreshText}>{t('course.refresh')}</Text>
          </Pressable>
        </View>
        {locationDenied ? <Text style={styles.error}>{t('course.permissionDenied')}</Text> : null}
        {courseError ? <Text style={styles.error}>{courseError}</Text> : null}
        {loadingCourses ? <ActivityIndicator /> : null}
        <Pressable style={styles.courseSelect} onPress={() => setCoursePickerOpen(true)} disabled={!courses.length}>
          <Text style={styles.courseSelectText}>{selectedCourse ? selectedCourse.name : t('course.select')}</Text>
        </Pressable>
        <View style={styles.roundMetaRow}>
          <Pressable
            style={styles.roundDescriptionButton}
            onPress={() => setRoundDescriptionOpen(true)}
            accessibilityLabel={t('players.roundDescriptionButton')}
          >
            <Text style={styles.roundDescriptionButtonText}>⋯</Text>
          </Pressable>
          <TextInput
            value={roundName}
            onChangeText={setRoundName}
            placeholder={t('players.roundNamePlaceholder')}
            placeholderTextColor="#7f8ca3"
            style={styles.roundNameInput}
          />
        </View>
        <View style={styles.playDateRow}>
          <Text style={styles.playDateLabel}>{t('players.playDateLabel')}</Text>
          <Pressable style={styles.playDateButton} onPress={() => setPlayDatePickerOpen(true)}>
            <Text style={styles.playDateButtonText}>{roundPlayDate}</Text>
          </Pressable>
        </View>
        <View style={styles.holesCountRow}>
          <Text style={styles.holesCountLabel}>{t('players.holesCountLabel')}</Text>
          <View style={styles.holesCountOptions}>
            {[9, 18].map((count) => (
              <Pressable
                key={count}
                style={[styles.holesCountOption, holesCount === count && styles.holesCountOptionActive]}
                onPress={() => setHolesCount(count)}
                accessibilityLabel={t('players.holesCountOptionLabel', { count })}
              >
                <Text style={[styles.holesCountText, holesCount === count && styles.holesCountTextActive]}>
                  {t('players.holesCountOptionValue', { count })}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      </View>
      <DatePicker
        modal
        open={playDatePickerOpen}
        date={parseDateInput(roundPlayDate) ?? new Date()}
        mode="date"
        onConfirm={(date) => {
          setRoundPlayDate(formatDateInput(date));
          setPlayDatePickerOpen(false);
        }}
        onCancel={() => setPlayDatePickerOpen(false)}
      />
      <View style={styles.playerList}>
        {players.map((player, index) => {
          const isCurrentUserRow = currentUserPlayerId && player.id === currentUserPlayerId;
          return (
            <View key={player.id} style={styles.playerRow}>
              {debug ? (
                <View style={styles.playerLabelRow}>
                  <Text
                    style={[
                      styles.playerDebugLabel,
                      isCurrentUserRow && styles.playerDebugLabelCurrent,
                    ]}
                  >
                    {isCurrentUserRow
                      ? `userId=${user?.id ?? ''} playerId=${player.id}`
                      : `playerId=${player.id}`}
                  </Text>
                </View>
              ) : null}
            {isCurrentUserRow ? (
              <View style={[styles.playerSelect, styles.playerSelectLocked]}>
                <Text style={styles.playerSelectText}>{player.name}</Text>
              </View>
            ) : (
              <Pressable
                style={styles.playerSelect}
                onPress={() => {
                  setPickerIndex(index);
                  setPlayerPickerOpen(true);
                }}
              >
                <Text style={styles.playerSelectText}>
                  {player.name || t('home.namePlaceholder')}
                </Text>
              </Pressable>
            )}
              <TextInput
                value={player.scp}
                onChangeText={(value) =>
                  setPlayers((prev) =>
                    prev.map((p, idx) => (idx === index ? { ...p, scp: value } : p))
                  )
                }
                placeholder={t('home.scpPlaceholder')}
                placeholderTextColor="#7f8ca3"
                keyboardType="numeric"
                style={styles.scpInput}
              />
              {isCurrentUserRow ? (
                <View style={styles.removeButtonSpacer} />
              ) : (
                <Pressable style={styles.removeButton} onPress={() => removePlayerRow(index)}>
                  <Text style={styles.removeButtonText}>✕</Text>
                </Pressable>
              )}
            </View>
          );
        })}
        <Pressable
          style={[styles.addButton, players.length >= 12 && styles.addButtonDisabled]}
          onPress={addPlayerRow}
          disabled={players.length >= 12}>
          <Text style={styles.addButtonText}>
            {players.length >= 12 ? t('home.maxPlayers') : t('home.addPlayers')}
          </Text>
        </Pressable>
        {playerError ? <Text style={styles.error}>{playerError}</Text> : null}
      </View>
      <Pressable
        style={[styles.primaryButton, savingPlayers && styles.primaryButtonDisabled]}
        onPress={handleSelectGames}
        disabled={savingPlayers}>
        <Text style={styles.primaryButtonText}>{savingPlayers ? t('home.saving') : t('selectGames.title')}</Text>
      </Pressable>
      <Modal visible={coursePickerOpen} transparent animationType="fade" onRequestClose={() => setCoursePickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setCoursePickerOpen(false)}>
          <View style={styles.modalCard}>
            <FlatList
              data={courses}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <Pressable
                  style={[
                    styles.courseOption,
                    item.id === selectedCourseId && styles.courseOptionActive,
                  ]}
                  onPress={() => {
                    setSelectedCourseId(item.id);
                    setCoursePickerOpen(false);
                  }}>
                  <Text style={styles.courseOptionTitle}>{item.name}</Text>
                  <Text style={styles.courseOptionSubtitle}>{item.address}</Text>
                </Pressable>
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
            />
          </View>
        </Pressable>
      </Modal>
      <Modal visible={roundDescriptionOpen} transparent animationType="fade" onRequestClose={() => setRoundDescriptionOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setRoundDescriptionOpen(false)}>
          <Pressable style={styles.modalCard} onPress={() => null}>
            <Text style={styles.modalTitle}>{t('players.roundDescriptionTitle')}</Text>
            <TextInput
              value={roundDescription}
              onChangeText={setRoundDescription}
              placeholder={t('players.roundDescriptionPlaceholder')}
              placeholderTextColor="#7f8ca3"
              multiline
              style={styles.roundDescriptionInput}
            />
            <Pressable style={styles.modalCloseButton} onPress={() => setRoundDescriptionOpen(false)}>
              <Text style={styles.modalCloseText}>{t('menu.close')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        visible={playerPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setPlayerPickerOpen(false)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setPlayerPickerOpen(false)}>
          <View style={styles.modalCard}>
            <FlatList
              data={availablePlayers}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <Pressable
                  style={styles.courseOption}
                  onPress={() => {
                    if (pickerIndex !== null) {
                      selectExistingPlayer(pickerIndex, item);
                    }
                    setPlayerPickerOpen(false);
                  }}
                >
                  <Text style={styles.courseOptionTitle}>{item.name}</Text>
                  <Text style={styles.courseOptionSubtitle}>{item.id}</Text>
                </Pressable>
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
              ListEmptyComponent={
                <Text style={styles.courseOptionSubtitle}>{t('players.loadError')}</Text>
              }
            />
          </View>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: '#0c1328',
  },
  container: {
    flexGrow: 1,
    padding: 24,
    gap: 16,
    backgroundColor: '#0c1328',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#f8fafc',
    textAlign: 'center',
  },
  headlineDebug: {
    textAlign: 'center',
    color: '#ef4444',
    fontSize: 12,
    fontWeight: '600',
  },
  tourName: {
    textAlign: 'center',
    color: '#34d399',
    fontWeight: '600',
  },
  roundsCard: {
    borderRadius: 16,
    padding: 16,
    backgroundColor: '#0f1c3c',
    borderWidth: 1,
    borderColor: '#24346b',
    gap: 10,
    overflow: 'visible',
    zIndex: 10,
    elevation: 2,
  },
  roundsHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roundsTitle: {
    color: '#d5e1ff',
    fontWeight: '700',
  },
  finishedRoundsBlock: {
    gap: 8,
    paddingVertical: 6,
  },
  finishedRoundsTitle: {
    color: '#ffd972',
    fontWeight: '700',
  },
  roundRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    backgroundColor: '#0b1224',
    position: 'relative',
    overflow: 'visible',
    zIndex: 1,
  },
  roundRowMenuOpen: {
    zIndex: 30,
  },
  roundRowFinished: {
    backgroundColor: 'rgba(255, 217, 114, 0.12)',
    borderColor: 'rgba(255, 217, 114, 0.4)',
  },
  roundContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  roundMenuButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundMenuIcon: {
    color: '#e5edff',
    fontSize: 18,
    lineHeight: 18,
    fontWeight: '700',
  },
  roundMenu: {
    position: 'absolute',
    top: 38,
    left: 8,
    backgroundColor: '#0f1c3c',
    borderRadius: 12,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#24346b',
    gap: 6,
    zIndex: 50,
    elevation: 8,
  },
  roundMenuItem: {
    paddingVertical: 6,
  },
  roundMenuText: {
    color: '#e5edff',
    fontWeight: '600',
  },
  roundName: {
    color: '#e5edff',
    fontWeight: '600',
  },
  roundMeta: {
    color: '#9fb3ff',
    fontSize: 12,
  },
  roundOpen: {
    color: '#34d399',
    fontWeight: '700',
  },
  roundResults: {
    color: '#ffd972',
    fontWeight: '700',
  },
  courseCard: {
    borderRadius: 16,
    padding: 16,
    backgroundColor: '#0f1c3c',
    borderWidth: 1,
    borderColor: '#24346b',
    zIndex: 0,
  },
  courseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  courseLabel: {
    color: '#d5e1ff',
    fontWeight: '600',
  },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
    backgroundColor: '#1f2d4f',
  },
  refreshText: {
    color: '#d5e1ff',
    fontWeight: '600',
  },
  refreshButtonDisabled: {
    opacity: 0.5,
  },
  error: {
    color: '#ff6666',
    marginBottom: 8,
  },
  courseSelect: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#33456d',
    padding: 12,
    backgroundColor: '#0b1224',
  },
  courseSelectText: {
    color: '#e5edff',
    fontWeight: '600',
  },
  roundMetaRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  playDateRow: {
    marginTop: 10,
    gap: 6,
  },
  playDateLabel: {
    color: '#c7d2fe',
    fontWeight: '600',
  },
  playDateButton: {
    alignSelf: 'flex-start',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#33456d',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#0b1224',
  },
  playDateButtonText: {
    color: '#e5edff',
    fontWeight: '700',
  },
  holesCountRow: {
    marginTop: 10,
    gap: 10,
  },
  holesCountLabel: {
    color: '#c7d2fe',
    fontWeight: '600',
  },
  holesCountOptions: {
    flexDirection: 'row',
    gap: 8,
  },
  holesCountOption: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#2b376f',
    backgroundColor: '#0b1531',
  },
  holesCountOptionActive: {
    borderColor: '#38bdf8',
    backgroundColor: '#0c3a5f',
  },
  holesCountText: {
    color: '#c7d2fe',
    fontWeight: '600',
  },
  holesCountTextActive: {
    color: '#e0f2fe',
  },
  roundDescriptionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#33456d',
    backgroundColor: '#0b1224',
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundDescriptionButtonText: {
    color: '#e5edff',
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 18,
  },
  roundNameInput: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#33456d',
    paddingVertical: 8,
    paddingHorizontal: 12,
    color: '#e5edff',
    backgroundColor: '#0b1224',
  },
  playerList: {
    gap: 12,
  },
  playerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  playerLabel: {
    width: 70,
    color: '#d5e1ff',
    fontWeight: '600',
  },
  playerLabelRow: {
    width: 90,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
  },
  playerDebugLabel: {
    color: '#7f8ca3',
    fontSize: 10,
  },
  playerDebugLabelCurrent: {
    color: '#ef4444',
  },
  input: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#33456d',
    backgroundColor: '#0b1224',
    color: '#e5edff',
  },
  playerSelect: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#33456d',
    backgroundColor: '#0b1224',
  },
  playerSelectDisabled: {
    borderColor: '#1f2d4f',
    backgroundColor: '#0a1022',
  },
  playerSelectLocked: {
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
  playerSelectText: {
    color: '#e5edff',
    fontWeight: '600',
  },
  scpInput: {
    width: 90,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#33456d',
    backgroundColor: '#0b1224',
    color: '#e5edff',
  },
  removeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#ff6666',
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeButtonSpacer: {
    width: 36,
    height: 36,
  },
  removeButtonDisabled: {
    backgroundColor: '#2b3147',
  },
  removeButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  addButton: {
    marginTop: 4,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: '#34d399',
    borderWidth: 1,
    borderColor: '#1aa374',
  },
  addButtonDisabled: {
    backgroundColor: '#1f2d4f',
    borderColor: '#24346b',
  },
  addButtonText: {
    color: '#0c1328',
    fontWeight: '700',
  },
  primaryButton: {
    marginTop: 'auto',
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: '#34d399',
    alignItems: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    color: '#0c1328',
    fontWeight: '800',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    backgroundColor: '#0f1c3c',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#24346b',
    padding: 12,
    maxHeight: '70%',
  },
  modalTitle: {
    color: '#f8fafc',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  modalCloseButton: {
    marginTop: 12,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#1f2d4f',
  },
  modalCloseText: {
    color: '#e5edff',
    fontWeight: '700',
  },
  roundDescriptionInput: {
    minHeight: 120,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#33456d',
    paddingVertical: 10,
    paddingHorizontal: 12,
    color: '#e5edff',
    backgroundColor: '#0b1224',
    textAlignVertical: 'top',
  },
  courseOption: {
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  courseOptionActive: {
    backgroundColor: '#1f2d4f',
  },
  courseOptionTitle: {
    color: '#e5edff',
    fontWeight: '600',
  },
  courseOptionSubtitle: {
    color: '#9fb3ff',
    fontSize: 12,
  },
  separator: {
    height: 1,
    backgroundColor: '#1b2b4f',
  },
});

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
